<template>
  <ArticlesHeader
    :loading="loading"
    :title="header"
    :subtitle="subheader"
    :country="countryObj"
    events-identifier="city-articles-info"
  />

  <JournalCountries
    :loading="loading"
    :list="cities"
    :slanted-text="countryObj?.name5"
    link-type="city"
    seo-prefix="geosubject-articles-info"
    geo-type="country"
    :geo-subject="countryObj"
  />

  <Wrap>
    <MosaicListView
      class="articles-mosaic-list"
      :items="journalContent.articles"
      :more-button="journalContent.hasNext"
      more-button-text="Показать ещё"
      more-data-test="destination-articles-btn-read-more"
      :loading="journalContent.loading"
      no-last-item-margin
      :number-of-skeleton-cards="2"
      @load-more="loadMore"
    >
      <template #default="{ item, mode, position }">
        <a
          :key="position"
          v-view-article-tracker="getParams(item)"
          class="article-card-wrapper"
          :href="`/journal/articles/${item.slug}/`"
          target="_blank"
          @click="track(item)"
        >
          <Card
            :item="item"
            :mode="mode"
            :locale="ruLocal"
          />
        </a>
      </template>
      <template #skeleton="{ mode }">
        <Skeleton
          :mode="mode"
          :loading="loading"
        />
      </template>
    </MosaicListView>
  </Wrap>

  <div class="exp-cards">
    <ExperiencesList
      :subject-geo-id="countryObj?.id"
      :subject-geo-name="countryObj?.name"
      :in-obj-phrase="countryObj?.inObjPhrase"
      :general-page-data-loading="loading"
    >
      <ExperienceListing
        :skeletons-count="3"
        more-button
        show-location
        geo-type="country"
        :geo-subject="countryObj"
        :more-button-text="t('seeAll')"
        :general-page-data-loading="loading"
        analytic-events-prefix="geosubject-articles-info"
        @load-more="goToDestination"
      />
    </ExperiencesList>
  </div>

  <SubscriptionBlock
    api-url=""
    :analytics-api-url="VITE_RUNTIME_SOGU_SERVICE_URL"
    page-type="listing"
  />
</template>

<script lang="ts" setup>
  import { useI18n } from '@experience/i18n';
  import { Card, Skeleton } from '@experience/mosaic-list-article-card-package';
  import { MosaicListView } from '@experience/mosaic-list-view-package';
  import { SubscriptionBlock } from '@experience/subscription-block-package';
  import { useHead } from '@unhead/vue';
  import { ru as ruLocal } from 'date-fns/locale';
  import { useRuntimeConfig } from 'virtual:runtime-config';
  import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue';
  import { useRoute } from 'vue-router';
  import { trackEvent, useViewTracker } from '@/services/analytics';
  import { useBootstrap } from '@/services/bootstrap';
  import { MF_NAME } from '@/app/const';
  import ExperienceListing from '@/features/ExperienceListing.vue';
  import JournalCountries from '@/features/JournalCountries/ui/JournalCountries.vue';
  import { useJournalContent } from '@/entities/journalContent';
  import { useSeoEntity } from '@/entities/seo';
  import type { ArticleInList } from '@/shared/models/articleInList';
  import type { GeoItem } from '@/shared/models/journal';
  import Wrap from '@/shared/ui/Wrap.vue';

  import { ArticlesHeader } from './features/articlesHeader';
  import { ExperiencesList } from './features/experiencesList/index';

  import { ru } from './i18n/ru';

  const { VITE_RUNTIME_SOGU_SERVICE_URL } = useRuntimeConfig()[MF_NAME];
  const route = useRoute();

  const { t } = useI18n({ inheritLocale: true, messages: { ru } });

  const seoEntity = useSeoEntity();

  const bootstrap = useBootstrap();
  const journalContent = useJournalContent();

  const vViewArticleTracker = useViewTracker('geosubject-articles-info:articles:article.view');

  const cities = ref<GeoItem[]>([]);
  const countryObj = ref();
  const loading = ref(true);

  const slug = computed(() => String(route.params.slug).toLowerCase());
  const header = computed(() => {
    return seoEntity.seo?.h1 ? seoEntity.seo.h1 : '';
  });

  const subheader = computed(() => seoEntity.seo?.h2 ?? '');
  const title = computed(() => seoEntity.seo?.title ?? '');
  const description = computed(() => seoEntity.seo?.description ?? '');
  const ogImage = computed(() => journalContent.articles[0]?.photo ?? '');

  useHead({
    title,
    link: [
      {
        rel: 'canonical',
        href: `${window.location.origin}${window.location.pathname}`,
      },
    ],
    meta: [
      {
        name: 'description',
        content: () => description.value,
      },
      {
        property: 'og:title',
        content: () => title.value,
      },
      {
        property: 'og:description',
        content: () => description.value,
      },
      {
        property: 'og:image',
        content: () => ogImage.value,
      },
      {
        property: 'og:url',
        content: () => `${window.location.origin}${window.location.pathname}`,
      },
    ],
  });

  const getParams = (item: ArticleInList) => ({
    subject_geo_id: countryObj.value.id,
    subject_geo_name: countryObj.value.name,
    subject_geo_type: 'country',
    arcticle_title: item.title,
    arcticle_id: item.id,
  });

  const track = async (item: ArticleInList) => {
    await trackEvent('geosubject-articles-info:articles:article.click', getParams(item));
  };

  onBeforeMount(async () => {
    try {
      const { cities: citiesData } = await bootstrap.fetchData('country', slug.value, 15);

      cities.value = citiesData?.cities || [];
      countryObj.value = citiesData?.countryObj || {};
    } finally {
      loading.value = false;
    }

    trackEvent('geosubject-articles-info.pageview', {
      subject_geo_id: countryObj.value.id,
      subject_geo_name: countryObj.value.name,
      subject_geo_type: 'country',
    });
  });

  onBeforeUnmount(() => {
    bootstrap.resetEntity();
  });

  const loadMore = () => {
    trackEvent('geosubject-articles-info:arcticles:show-all.click', {
      subject_geo_id: countryObj.value.id,
      subject_geo_name: countryObj.value.name,
      subject_geo_type: 'country',
    });

    journalContent.getArticles({ slugType: 'country', slug: slug.value, pageSize: 15 });
  };

  const goToDestination = async () => {
    await trackEvent('geosubject-articles-info:experiences:show-all.click', {
      subject_geo_id: countryObj.value.id,
      subject_geo_name: countryObj.value.name,
      subject_geo_type: 'country',
    });

    window.location.href = `/destinations/${slug.value}/`;
  };
</script>

<style lang="scss" scoped>
  a {
    text-decoration: none;
  }

  .articles-mosaic-list {
    margin-top: 24px;

    @include tablet {
      margin-top: 16px;
    }
  }

  .exp-cards {
    margin-top: 48px;

    @include tablet {
      margin-top: 80px;
    }

    @include mobile {
      margin-top: 40px;
    }
  }
</style>
